import { AlertTriangle, Clock, AlertOctagon, Mail, X, Shield, Send, Euro, CheckCircle2, XCircle, ReceiptText, Wrench, FileText } from 'lucide-react'
import { cn } from '@fleetops/ui'
import { relativeTime } from '@/lib/format'
import type { AppNotification, NotificationType } from '@fleetops/types'

const TYPE_CONFIG: Record<NotificationType, { icon: React.ReactNode; color: string }> = {
  damage_reported: { icon: <AlertTriangle className="h-3.5 w-3.5" />, color: 'text-orange-500 bg-orange-50' },
  sla_warning: { icon: <Clock className="h-3.5 w-3.5" />, color: 'text-amber-500 bg-amber-50' },
  sla_breached: { icon: <AlertOctagon className="h-3.5 w-3.5" />, color: 'text-red-500 bg-red-50' },
  user_invited: { icon: <Mail className="h-3.5 w-3.5" />, color: 'text-blue-500 bg-blue-50' },
  compliance_alert: { icon: <Shield className="h-3.5 w-3.5" />, color: 'text-yellow-600 bg-yellow-50' },
  estimate_escalated: { icon: <Send className="h-3.5 w-3.5" />, color: 'text-amber-600 bg-amber-50' },
  estimate_received: { icon: <Euro className="h-3.5 w-3.5" />, color: 'text-emerald-600 bg-emerald-50' },
  estimate_approved: { icon: <CheckCircle2 className="h-3.5 w-3.5" />, color: 'text-emerald-600 bg-emerald-50' },
  estimate_rejected: { icon: <XCircle className="h-3.5 w-3.5" />, color: 'text-red-600 bg-red-50' },
  credit_note_required: { icon: <ReceiptText className="h-3.5 w-3.5" />, color: 'text-violet-600 bg-violet-50' },
  credit_note_submitted: { icon: <ReceiptText className="h-3.5 w-3.5" />, color: 'text-emerald-600 bg-emerald-50' },
  damage_critical: { icon: <AlertOctagon className="h-3.5 w-3.5" />, color: 'text-red-600 bg-red-50' },
  maintenance_due: { icon: <Wrench className="h-3.5 w-3.5" />, color: 'text-blue-600 bg-blue-50' },
  invoice_uploaded: { icon: <FileText className="h-3.5 w-3.5" />, color: 'text-slate-600 bg-slate-100' },
}

export function NotificationPanel({
  notifications,
  unreadCount,
  onMarkAllRead,
  onMarkRead,
  onOpen,
  onClose,
}: {
  notifications: AppNotification[]
  unreadCount: number
  onMarkAllRead: () => void
  onMarkRead: (id: string) => void
  // Navigate to what the notification is about (a damage, compliance…).
  onOpen: (n: AppNotification) => void
  onClose: () => void
}) {
  return (
    <div className="flex w-80 flex-col rounded-xl border border-slate-200 bg-white shadow-xl">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <span className="text-sm font-semibold text-slate-800">Notificações</span>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllRead}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              Marcar como lidas
            </button>
          )}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="flex h-24 items-center justify-center">
            <p className="text-sm text-slate-400">Sem notificações</p>
          </div>
        ) : (
          notifications.map((n) => {
            const cfg = TYPE_CONFIG[n.type] ?? { icon: null, color: 'text-slate-500 bg-slate-100' }
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => { if (!n.read) onMarkRead(n.id); onOpen(n) }}
                className={cn(
                  'flex w-full gap-3 border-b border-slate-50 px-4 py-3 text-left last:border-0 hover:bg-slate-50',
                  !n.read && 'border-l-2 border-l-blue-500 bg-blue-50/40 hover:bg-blue-50',
                )}
              >
                <div className={cn('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full', cfg.color)}>
                  {cfg.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-slate-800">{n.title}</p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{n.message}</p>
                  <p className="mt-1 text-[10px] text-slate-400">{relativeTime(n.createdAt)}</p>
                </div>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
